import { observable, runInAction } from 'mobx'
import Member from 'iam/models/Member'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import Job from 'jobs/Job'
import { Id } from 'types/definitions'
import { ignore404 } from 'utils/error-util'
import { FlockAPI } from 'flock/API'
import isFlockRelationshipManagerEnabled from 'flock/flockRelationshipManagerFeatureToggle'
import { CSDeskAPI } from './API'

/**
 * Store for the relationship manager assigned to a job
 */
export default class RelationManagerStore {
  private readonly rmByJobId = observable.map<Id, Member | null>()

  constructor(
    private readonly api: CSDeskAPI,
    private readonly flockAPI: FlockAPI,
    private readonly primeMemberByPublicId: PrimeMemberByPublicId,
  ) {}

  /**
   * Get RelationManager assigned to job
   */
  getForJob(jobId: Id): Member | null | undefined {
    return this.rmByJobId.get(jobId)
  }

  /**
   * Fetches the job's RM from csdesk or flock.
   */
  async fetchForJob(job: Job): Promise<Member | null> {
    try {
      const userPublicId = await this.fetchUserPublicIdForJob(Id.assert(job.id))

      const member = userPublicId
        ? this.primeMemberByPublicId({
            workspace_id: job.workspace.id,
            user_public_id: userPublicId,
          })
        : null

      return runInAction(() => {
        this.rmByJobId.set(job.id, member)
        return member
      })
    } catch (err) {
      console.error(err)
      return null
    }
  }

  private async fetchUserPublicIdForJob(jobId: string): Promise<string | null> {
    if (isFlockRelationshipManagerEnabled()) {
      const response = await this.flockAPI.relationshipManager
        .getForJob(jobId)
        .catch(ignore404)
      return response ? response.user_public_id : null
    }

    const response = await this.api.relationManager
      .getForJob(jobId)
      .catch(ignore404)
    return response ? response.data.userPublicId : null
  }
}
